'use client';

import { useCallback, useEffect, useReducer, useRef } from 'react';
import type { Attachment } from '@langbridge-builder/design-system';
import type { ChatClient } from '../transport/types';
import { chatReducer, initialChatState, type ChatMessage, type InterruptDecision } from './reducer';
import { clearMessages, loadMessages, saveMessages } from './persist';

export interface UseChatOptions {
  client: ChatClient;
  /**
   * When set, the conversation transcript is persisted to localStorage under this
   * key and restored (frozen, read-only) on mount — surviving a page refresh.
   * Use a per-conversation key (e.g. `widget:<agentId>`). Omit for ephemeral chats.
   */
  persistKey?: string;
  /** Storage backend override (defaults to window.localStorage). Mainly for tests. */
  persistStorage?: Storage;
}

/**
 * Drives one conversation against a ChatClient. Owns the AG-UI reducer, the
 * in-flight AbortController, and the last-query ref (for retry). abort() marks
 * the state 'aborted' immediately; the client resolves early on the same signal.
 */
export function useChat({ client, persistKey, persistStorage }: UseChatOptions) {
  const [state, dispatch] = useReducer(chatReducer, initialChatState);
  const abortRef = useRef<AbortController | null>(null);
  const lastTextRef = useRef('');
  const lastAttachmentsRef = useRef<Attachment[] | undefined>(undefined);
  const restoredKeyRef = useRef<string | null>(null);
  const phaseRef = useRef(state.phase);
  phaseRef.current = state.phase;
  const interruptRef = useRef(state.interrupt);
  interruptRef.current = state.interrupt;

  const run = useCallback(
    async (
      text: string,
      opts?: { attachments?: Attachment[]; action?: Record<string, unknown>; retry?: boolean },
    ) => {
      if (phaseRef.current === 'running') return;
      const { attachments, action, retry } = opts ?? {};
      if (!action) {
        lastTextRef.current = text;
        lastAttachmentsRef.current = attachments;
      }
      const display = action ? `⮐ ${(action.name as string) ?? 'action'}` : text;
      dispatch({ kind: 'start', text: display, attachments, fromAction: !!action, retry });

      const ac = new AbortController();
      abortRef.current = ac;
      try {
        await client.run(
          { text, attachments, action },
          (event) => dispatch({ kind: 'event', event }),
          ac.signal,
        );
        dispatch({ kind: 'finish' });
      } catch (e) {
        if (ac.signal.aborted) dispatch({ kind: 'aborted' });
        else dispatch({ kind: 'error', msg: e instanceof Error ? e.message : String(e) });
      } finally {
        if (abortRef.current === ac) abortRef.current = null;
      }
    },
    [client],
  );

  const send = useCallback(
    (text: string, attachments?: Attachment[]) => {
      const t = text.trim();
      if (t || attachments?.length) void run(t, { attachments });
    },
    [run],
  );

  const sendAction = useCallback(
    (action: Record<string, unknown>) => {
      void run('', { action });
    },
    [run],
  );

  const retry = useCallback(() => {
    if (lastTextRef.current || lastAttachmentsRef.current?.length)
      void run(lastTextRef.current, { attachments: lastAttachmentsRef.current, retry: true });
  }, [run]);

  /** Answer a pending HIL interrupt and resume the paused turn (no new message). */
  const respondInterrupt = useCallback(
    async (decisions: InterruptDecision[]) => {
      if (phaseRef.current !== 'interrupted') return;
      // Pair each decision with its action's standard interrupt id (same order)
      // so the transport can build the AG-UI `resume: ResumeEntry[]`.
      const interruptIds = (interruptRef.current?.actions ?? []).map((a) => a.id ?? '');
      dispatch({ kind: 'resume' });
      const ac = new AbortController();
      abortRef.current = ac;
      try {
        await client.run(
          { text: '', resume: { decisions, interruptIds } },
          (event) => dispatch({ kind: 'event', event }),
          ac.signal,
        );
        dispatch({ kind: 'finish' });
      } catch (e) {
        if (ac.signal.aborted) dispatch({ kind: 'aborted' });
        else dispatch({ kind: 'error', msg: e instanceof Error ? e.message : String(e) });
      } finally {
        if (abortRef.current === ac) abortRef.current = null;
      }
    },
    [client],
  );

  const abort = useCallback(() => {
    if (phaseRef.current !== 'running') return;
    abortRef.current?.abort();
    dispatch({ kind: 'aborted' });
  }, []);

  const reset = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    if (persistKey) clearMessages(persistKey, persistStorage);
    dispatch({ kind: 'reset' });
  }, [persistKey, persistStorage]);

  /**
   * Load a restored past-session transcript as the conversation history
   * (read-only). Cancels any in-flight run first. Pair with an AguiChatClient
   * seeded with the session's threadId so the user can continue the conversation.
   */
  const hydrate = useCallback((messages: ChatMessage[]) => {
    abortRef.current?.abort();
    abortRef.current = null;
    dispatch({ kind: 'hydrate', messages });
  }, []);

  // Restore a persisted transcript on mount / key change (frozen read-only via
  // hydrate). Re-runs when persistKey changes (e.g. switching agent/conversation).
  useEffect(() => {
    if (!persistKey || restoredKeyRef.current === persistKey) return;
    const restored = loadMessages(persistKey, persistStorage);
    if (restored?.length) dispatch({ kind: 'hydrate', messages: restored });
    restoredKeyRef.current = persistKey;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [persistKey]);

  // Persist settled turns. Skip while running (mid-stream), before restore
  // completes for this key, and for empty transcripts (never clobber with []).
  useEffect(() => {
    if (!persistKey || restoredKeyRef.current !== persistKey) return;
    if (state.phase === 'running') return;
    if (state.messages.length === 0) return;
    saveMessages(persistKey, state.messages, persistStorage);
  }, [persistKey, persistStorage, state.messages, state.phase]);

  return {
    messages: state.messages,
    phase: state.phase,
    error: state.error,
    activity: state.activity,
    interrupt: state.interrupt,
    send,
    sendAction,
    retry,
    respondInterrupt,
    abort,
    reset,
    hydrate,
  };
}
